import useAuthState, { UserProfile } from '@/states/auth.state';
import { KeycloakUserInfo } from './keycloak';
import { env } from '@/utils/constants/env';

export interface UnifiedUserInfo {
  id: string;
  name: string;
  email: string;
  username?: string;
  avatar_url?: string;
  is_admin?: boolean;
}

/**
 * Get unified user information regardless of auth method
 */
export const useUnifiedUserInfo = (): UnifiedUserInfo | null => {
  const { userProfile, keycloakUserInfo, authMethod } = useAuthState();

  if (authMethod === 'keycloak' && keycloakUserInfo) {
    return {
      id: keycloakUserInfo.sub,
      name: keycloakUserInfo.name || `${keycloakUserInfo.given_name} ${keycloakUserInfo.family_name}`.trim(),
      email: keycloakUserInfo.email,
      username: keycloakUserInfo.preferred_username,
      // Keycloak doesn't provide avatar_url or is_admin by default
      avatar_url: undefined,
      is_admin: undefined,
    };
  }

  if (authMethod === 'alurkerja_sso' && userProfile) {
    return {
      id: userProfile.id.toString(),
      name: userProfile.name,
      email: userProfile.email,
      username: userProfile.email, // AlurKerja uses email as username
      avatar_url: userProfile.avatar_url,
      is_admin: userProfile.is_admin,
    };
  }

  return null;
};

/**
 * Get user email for API calls that require username/email
 */
export const useUserEmail = (): string | null => {
  const userInfo = useUnifiedUserInfo();
  return userInfo?.email || null;
};

/**
 * Get user display name
 */
export const useUserDisplayName = (): string | null => {
  const userInfo = useUnifiedUserInfo();
  return userInfo?.name || null;
};

/**
 * Check if user is admin (only available for AlurKerja SSO)
 */
export const useIsUserAdmin = (): boolean => {
  const userInfo = useUnifiedUserInfo();
  return userInfo?.is_admin || false;
};

/**
 * Get avatar URL (only available for AlurKerja SSO)
 * Handles Laravel storage URLs by prefixing with apiBaseUrl
 */
export const useUserAvatarUrl = (): string | null => {
  const userInfo = useUnifiedUserInfo();
  const avatarUrl = userInfo?.avatar_url;

  if (!avatarUrl) return null;

  // If already a full URL, return as is
  if (avatarUrl.startsWith('http')) return avatarUrl;

  // If Laravel storage path, prefix with apiBaseUrl
  if (avatarUrl.includes('/storage/')) {
    return env.apiBaseUrl + avatarUrl;
  }

  // Otherwise return as is (might be relative path or other format)
  return avatarUrl;
};
